import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { staffAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  IndianRupee, Users, Calendar, Download,
  Clock, CheckCircle, Wallet, AlertCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import ListPageHeader from '../../components/shared/ListPageHeader';
import ColumnFilter from '../../components/shared/ColumnFilter';

const PayrollPage = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staff, setStaff] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    role: '',
    salary: { min: '', max: '' },
    status: []
  });

  const [paymentForm, setPaymentForm] = useState({
    staff_id: '',
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'CASH',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([
      fetchStaff(),
      fetchPayrollRecords()
    ]);
    setLoading(false);
  };

  const fetchStaff = async () => {
    try {
      const { data } = await staffAPI.list();
      setStaff((data || []).map(s => ({
        ...s,
        name: s.name || s.full_name || s.email || 'Unknown',
        phone: s.phone || s.mobile || '',
        role_label: s.role_label || s.role_code || 'Staff',
        salary_amount: s.salary_amount || 0,
      })));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load staff list'));
    }
  };

  const fetchPayrollRecords = async () => {
    try {
      const { data } = await staffAPI.getPayrollSummary(selectedMonth);
      setPayrollRecords(data.records || []);
    } catch (error) {
      setError('Failed to load payroll data. Please try again.');
      setPayrollRecords([]);
      toast.error(getApiErrorMessage(error, 'Failed to load payroll data'));
    }
  };

  const handlePaySalary = async (e) => {
    e.preventDefault();
    try {
      await staffAPI.payDirect(paymentForm);
      toast.success('Salary paid successfully!');
      setShowPaymentModal(false);
      resetForm();
      fetchPayrollRecords();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to process salary payment'));
    }
  };

  const resetForm = () => {
    setPaymentForm({
      staff_id: '',
      amount: 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'CASH',
      notes: '',
    });
    setSelectedStaff(null);
  };

  const openPaymentModal = (staffMember, record) => {
    setSelectedStaff(staffMember);
    setPaymentForm({
      staff_id: staffMember.id,
      amount: Math.round((record?.net_salary || staffMember.salary_amount || 0) * 100) / 100,
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'CASH',
      notes: `Salary for ${selectedMonth}`,
    });
    setShowPaymentModal(true);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `Rs.${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `Rs.${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `Rs.${(amount / 1000).toFixed(1)}K`;
    return `Rs.${amount?.toFixed(0) || 0}`;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getInitialColor = (name) => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-emerald-100 text-emerald-700',
      'bg-purple-100 text-purple-700',
      'bg-amber-100 text-amber-700',
      'bg-rose-100 text-rose-700',
      'bg-cyan-100 text-cyan-700',
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  // Generate payroll data for each staff member
  const payrollData = staff.map(s => {
    const record = payrollRecords.find(r => r.staff_id === s.id) || {
      days_worked: 0,
      total_days: 26,
      deductions: 0,
      bonuses: 0,
      status: 'PENDING'
    };

    const baseSalary = s.salary_amount || 0;
    const perDaySalary = baseSalary / record.total_days;
    const earnedSalary = perDaySalary * record.days_worked;
    const netSalary = earnedSalary - (record.deductions || 0) + (record.bonuses || 0);

    return {
      ...s,
      ...record,
      base_salary: baseSalary,
      earned_salary: earnedSalary,
      net_salary: netSalary
    };
  });

  // Apply column filters
  const filteredPayroll = payrollData.filter(item => {
    const matchesName = !columnFilters.name ||
      item.name?.toLowerCase().includes(columnFilters.name.toLowerCase());

    const matchesRole = !columnFilters.role ||
      item.role_label?.toLowerCase().includes(columnFilters.role.toLowerCase());

    const salary = item.net_salary || 0;
    const matchesSalary = (!columnFilters.salary?.min || salary >= parseFloat(columnFilters.salary.min)) &&
      (!columnFilters.salary?.max || salary <= parseFloat(columnFilters.salary.max));

    const matchesStatus = columnFilters.status.length === 0 ||
      columnFilters.status.includes(item.status);

    return matchesName && matchesRole && matchesSalary && matchesStatus;
  });

  const totalSalary = filteredPayroll.reduce((sum, p) => sum + (p.net_salary || 0), 0);
  const paidCount = filteredPayroll.filter(p => p.status === 'PAID').length;
  const pendingCount = filteredPayroll.filter(p => p.status !== 'PAID').length;
  const paidAmount = filteredPayroll.filter(p => p.status === 'PAID').reduce((sum, p) => sum + (p.net_salary || 0), 0);

  const months = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    months.push({
      value: date.toISOString().slice(0, 7),
      label: date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
    });
  }

  return (
    <div className="h-full flex flex-col" data-testid="payroll-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Payroll Management"
        stats={[
          { icon: Users, label: 'Total Staff', value: staff.length, color: 'slate' },
          { icon: IndianRupee, label: 'Total Payroll', value: formatCompactCurrency(totalSalary), color: 'blue' },
          { icon: CheckCircle, label: 'Paid', value: `${paidCount} (${formatCompactCurrency(paidAmount)})`, color: 'emerald' },
          { icon: Clock, label: 'Pending', value: pendingCount, color: 'amber' },
        ]}
        ctaLabel="Export"
        onCtaClick={() => toast.info('Export feature coming soon')}
        ctaIcon={Download}
        secondaryAction={{
          label: new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
          icon: Calendar,
          onClick: () => {},
          isDropdown: true,
          dropdownContent: (
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48 bg-white border-0 shadow-none">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(month => (
                  <SelectItem key={month.value} value={month.value}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading payroll...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <p className="text-[14px] font-medium text-slate-700">Failed to load payroll</p>
              <p className="text-[13px] text-slate-400 mt-1">{error}</p>
              <button
                onClick={fetchData}
                className="mt-4 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Retry
              </button>
            </div>
          ) : filteredPayroll.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Wallet className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No payroll data found</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">Add staff members to start managing payroll</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '6%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="tl-th tl-th-teal">
                        <div className="flex items-center gap-1">
                          Staff Member
                          <ColumnFilter
                            type="text"
                            value={columnFilters.name}
                            onChange={(val) => setColumnFilters({...columnFilters, name: val})}
                            placeholder="Filter name..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal">
                        <div className="flex items-center gap-1">
                          Role
                          <ColumnFilter
                            type="text"
                            value={columnFilters.role}
                            onChange={(val) => setColumnFilters({...columnFilters, role: val})}
                            placeholder="Filter role..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal text-center">Days Worked</th>
                      <th className="tl-th tl-th-teal tl-th-right">Base Salary</th>
                      <th className="tl-th tl-th-teal tl-th-right">Deductions</th>
                      <th className="tl-th tl-th-teal tl-th-right">Bonuses</th>
                      <th className="tl-th tl-th-teal tl-th-right">
                        <div className="flex items-center justify-end gap-1">
                          Net Salary
                          <ColumnFilter
                            type="number"
                            value={columnFilters.salary}
                            onChange={(val) => setColumnFilters({...columnFilters, salary: val})}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal text-center">
                        <div className="flex items-center justify-center gap-1">
                          Status
                          <ColumnFilter
                            type="status"
                            value={columnFilters.status}
                            onChange={(val) => setColumnFilters({...columnFilters, status: val})}
                            options={[
                              { value: 'PAID', label: 'Paid' },
                              { value: 'PENDING', label: 'Pending' }
                            ]}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal">Action</th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredPayroll.map((item, index) => (
                      <tr
                        key={item.id}
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                      >
                        <td className="tl-td">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ${getInitialColor(item.name)}`}>
                              {getInitials(item.name)}
                            </div>
                            <div className="min-w-0">
                              <span className="tl-cell-primary truncate block">{item.name}</span>
                              <p className="text-[11px] text-slate-500 truncate">{item.phone}</p>
                            </div>
                          </div>
                        </td>
                        <td className="tl-td">
                          <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-[12px] font-medium">
                            {item.role_label || 'Staff'}
                          </span>
                        </td>
                        <td className="tl-td text-center">
                          <span className="text-[13px] font-semibold text-slate-800">
                            {item.days_worked || 0}
                          </span>
                          <span className="text-[11px] text-slate-500">/{item.total_days || 26}</span>
                        </td>
                        <td className="tl-td tl-td-right tl-cell-regular">
                          Rs.{(item.base_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="tl-td tl-td-right">
                          <span className="text-[13px] text-rose-600">-Rs.{(item.deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </td>
                        <td className="tl-td tl-td-right">
                          <span className="text-[13px] text-emerald-600">+Rs.{(item.bonuses || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </td>
                        <td className="tl-td tl-td-right">
                          <span className="text-[14px] font-bold text-slate-900">Rs.{(item.net_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </td>
                        <td className="tl-td text-center">
                          {item.status === 'PAID' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[11px] font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Paid
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[11px] font-medium">
                              <Clock className="w-3 h-3" />
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="tl-td">
                          {item.status !== 'PAID' && (
                            <button
                              onClick={() => openPaymentModal(item, item)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[11px] font-medium"
                            >
                              <Wallet className="w-3 h-3" />
                              Pay
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* TABLE FOOTER */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredPayroll.length}</span> staff for {new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                </p>
                <p>
                  Total Payable: <span className="font-medium text-slate-700">Rs.{totalSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => { setShowPaymentModal(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Pay Salary</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePaySalary} className="space-y-4 pt-4">
            {selectedStaff && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-semibold ${getInitialColor(selectedStaff.name)}`}>
                  {getInitials(selectedStaff.name)}
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-slate-800">{selectedStaff.name}</p>
                  <p className="text-[12px] text-slate-500">{selectedStaff.role_label || 'Staff'}</p>
                </div>
              </div>
            )}

            <div>
              <Label className="text-[13px] font-medium text-slate-700">Amount *</Label>
              <div className="relative mt-1.5">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })}
                  className="pl-9"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-[13px] font-medium text-slate-700">Payment Date *</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label className="text-[13px] font-medium text-slate-700">Payment Mode *</Label>
              <Select value={paymentForm.payment_mode} onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_mode: value })}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASH">Cash</SelectItem>
                  <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[13px] font-medium text-slate-700">Notes</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Optional notes..."
                className="mt-1.5"
              />
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                <Wallet className="w-4 h-4 mr-2" />
                Pay Rs.{paymentForm.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollPage;
